<template>
  <div
    v-if="displayedColors.length > 1"
    :class="[
      bemBlockMod({ scroll: plainImages, plain: usePlainImages }),
      'base-color-swatches',
      {
        'visually-hidden': visuallyHidden,
      },
    ]"
  >
    <ul
      v-drag-scroll="{ draggable: plainImages, overflowing: false }"
      :class="[
        bemElement('color-container'),
        'base-color-swatches__color-container',
      ]"
      @mousedown="handleRowMousedown"
      @dragstart="handleRowDragstart"
    >
      <li
        v-for="(color, index) in displayedColors"
        v-show="color.displayed"
        :key="index"
        :class="[bemElement('item'), 'base-color-swatches__item']"
        data-attr="color"
      >
        <button
          :class="[
            bemElement('swatch', { selected: isColorSelected(color) }),
            'base-color-swatches__swatch',
            isColorSelected(color)
              ? 'base-color-swatches__swatch--selected'
              : '',
          ]"
          :data-url="color.url"
          @click="handleSelectColor(color, $event)"
        >
          <span class="visually-hidden">{{ color.displayedValue }}</span>
          <img
            v-if="usePlainImages"
            :alt="getPlainImage(color).alt"
            :class="bemElement('plain-img')"
            :src="getPlainImage(color).url"
            loading="lazy"
            @error="handleImageError"
          />
          <img
            v-else-if="hasSwatchImage(color)"
            :alt="getSwatchImage(color).alt"
            :class="[
              bemElement('swatch-img'),
              'base-color-swatches__swatch-display',
              {
                loading: loadingImage,
              },
            ]"
            :src="getSwatchImage(color).url"
            loading="lazy"
            @error="handleImageError"
            @load="isImageLoaded"
          />
        </button>
      </li>
    </ul>
    <button
      v-if="overflowMaxColors"
      :class="[
        bemElement(expanded ? 'shrink' : 'expand'),
        expanded ? 'btn-minus' : 'btn-plus',
        expanded
          ? 'base-color-swatches__shrink'
          : 'base-color-swatches__expand',
      ]"
      @click="toggleExtended"
    />
  </div>
</template>

<script>
import { getColorCodeFromPid } from '../../common/helpers/productIdHelpers';
import EventBusConfig from '../config/eventBusConfig';
import bemClassesMixin from '../mixins/bemClassesMixin';
import productPropColorMixin from '../mixins/productPropColorMixin';
import sfraProductMixin from '../mixins/sfraProductMixin';
import VueDragScroll from '../plugins/vue-drag-scroll';

const MAX_DISPLAYED_SWATCHES = 3;
// px the mouse may move between press and release and still be a click
const DRAG_CLICK_TOLERANCE = 5;

const noImagePlaceholderColor = '#e0e0e0';
const noImagePlaceholder = `<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8'><rect width='8' height='8' fill='${noImagePlaceholderColor}'/></svg>`;
const noImagePlaceholderUrl = `data:image/svg+xml,${encodeURIComponent(
  noImagePlaceholder
)}`;
const noImage = {
  alt: '',
  title: '',
  url: noImagePlaceholderUrl,
};

export default {
  name: 'BaseProductColorSwatches',

  directives: { dragScroll: VueDragScroll },

  mixins: [bemClassesMixin, productPropColorMixin, sfraProductMixin],

  props: {
    visuallyHidden: {
      type: Boolean,
      default: false,
    },
    showCurrent: {
      type: Boolean,
      default: false,
    },
    productDetail: {
      type: Boolean,
      default: false,
    },
    preExpanded: {
      type: Boolean,
      default: false,
    },
    /**
     * PDP: every color in one row that scrolls sideways, with no «+», and as
     * plain thumbnails when all of them have one (`swatch-small` otherwise)
     */
    plainImages: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    expanded: false,
    selectedColor: undefined,
    initialColors: [],
    loadingImage: true,
    maxDisplayedColors: MAX_DISPLAYED_SWATCHES,
    rowMousedownX: null,
  }),

  computed: {
    overflowMaxColors() {
      return !this.plainImages && this.colors.length > this.maxDisplayedColors;
    },
    /**
     * All or nothing: thumbnails mixed with color squares are not shown
     * @returns {boolean} whether every color is painted with its plain image
     */
    usePlainImages() {
      return (
        this.plainImages &&
        this.getInitialColors().every((color) => this.getPlainImage(color))
      );
    },
    displayedColors() {
      const colors = this.getInitialColors();
      colors.map((color, index) => {
        const displayedColor = color;
        displayedColor.displayed = this.isColorDisplayed(color, index);
        return displayedColor;
      });
      return colors;
    },
  },

  mounted() {
    const productColorId = getColorCodeFromPid(this.product.id);
    const actualColor = this.colors.find(
      (color) => color.id === productColorId
    );
    this.selectedColor = actualColor;
    this.expanded = this.preExpanded;
    if (this.productDetail) {
      this.$eventBus.$on(
        EventBusConfig.events.TheProductDetails.colorSelected,
        this.updateInitialColors
      );
    }
  },

  beforeDestroy() {
    if (this.productDetail) {
      this.$eventBus.$off(
        EventBusConfig.events.TheProductDetails.colorSelected,
        this.updateInitialColors
      );
    }
  },

  methods: {
    /**
     * @param {Object} color - a color value
     * @returns {Object|false} its first plain image, false if it has none with a url
     */
    getPlainImage(color) {
      const plain = color.bylImages?.['plain.200']?.[0];
      return plain && plain.url ? plain : false;
    },
    getSwatchImage(color) {
      const swatch = this.hasSwatchImage(color)
        ? color.bylImages['swatch-small'][0]
        : false;
      return swatch && swatch.url ? swatch : false;
    },
    hasSwatchImage(color) {
      return (
        color.images &&
        color.images.swatch &&
        color.images.swatch.length > 0 &&
        color.images.swatch[0] !== undefined
      );
    },
    handleImageError(e) {
      const img = e.target;
      img.src = noImage.url;
    },
    isColorSelected(color) {
      return this.selectedColor?.id === color.id;
    },
    toggleExtended() {
      this.expanded = !this.expanded;
    },
    handleRowMousedown(event) {
      this.rowMousedownX = event.clientX;
    },
    /**
     * The row scrolls by dragging it (plainImages): the browser must not drag
     * the image instead
     * @param {DragEvent} event - the native drag of an image
     */
    handleRowDragstart(event) {
      if (this.plainImages) event.preventDefault();
    },
    /**
     * @param {MouseEvent} event - the click on a color
     * @returns {boolean} whether it ends a drag of the row rather than picks a color
     */
    isDragRelease(event) {
      return (
        this.plainImages &&
        event.detail > 0 && // 0: a keyboard click, which has no press
        this.rowMousedownX !== null &&
        Math.abs(event.clientX - this.rowMousedownX) > DRAG_CLICK_TOLERANCE
      );
    },
    handleSelectColor(color, event) {
      const dragged = this.isDragRelease(event);
      this.rowMousedownX = null;
      if (dragged) return;
      if (this.isColorSelected(color)) {
        event.stopPropagation();
      } else {
        if (this.productDetail) {
          this.selectedColor = color;
          this.$eventBus.$emit(
            EventBusConfig.events.TheProductDetails.colorSelected,
            color,
            this.findSfraProductContainer()
          );
        }
        this.$emit('select', color, this.expanded);
      }
    },
    getInitialColors() {
      return this.initialColors.length > 0 ? this.initialColors : this.colors;
    },
    isColorDisplayed(color, index) {
      return (
        this.plainImages ||
        this.expanded ||
        index < this.maxDisplayedColors ||
        this.isColorSelected(color)
      );
    },
    isImageLoaded() {
      this.loadingImage = false;
    },
    updateInitialColors() {
      this.initialColors = this.getInitialColors();
    },
  },
};
</script>
